/* eslint-disable complexity */
import {useIsConfirmed} from '@hconnect/tuc-processes/fields/isConfirmed/useIsConfirmed'
import {Box} from '@mui/material'
import React from 'react'

import {useOrderIntakeData} from '../../../../../../OrderIntake/hooks/useOrderIntakeData'
import {OrderConfirmationSection} from '../../../../shared/order-editor/components/fields/Confirm/OrderConfirmationSection'
import {ContactSection} from '../../../../shared/order-editor/components/fields/Contact/ContactSection'
import {DriverInstructionsField} from '../../../../shared/order-editor/components/fields/DriverInstructions/DriverInstructionsField'
import {NotesField} from '../../../../shared/order-editor/components/fields/Notes/NotesField'
import {PoNumberField} from '../../../../shared/order-editor/components/fields/PoNumber/PoNumberField'
import {RetarderField} from '../../../../shared/order-editor/components/fields/Retarder/RetarderField'
import {MaterialTestingField} from '../../../../shared/order-editor/components/fields/Testing/MaterialTestingField'
import {useDetailsScreenFields} from '../../../../shared/order-editor/hooks/useRegionConfig'
import {useRmcCountryFeatures} from '../../../../shared/order-editor/hooks/useRmcCountryFeatures'

/**
 * Details step of the RMC order stepper.
 * Allows the user to add additional details like order confirmation, PO number, driver instructions, notes, and retarder.
 */
export const DetailsStep: React.FC = () => {
  // Get user profile data for contact defaults
  const {userProfileCountry} = useOrderIntakeData()

  // Get country features for conditional rendering
  const regionFeatures = useRmcCountryFeatures()

  const {setIsConfirmed, canConfirmOrder, confirmationPinRequired} = useIsConfirmed()

  // Get consolidated fields for the details screen
  const detailsFields = useDetailsScreenFields()

  // Determine if confirmation section should be disabled
  const isConfirmationDisabled = !canConfirmOrder || confirmationPinRequired

  const handleConfirmationChange = (confirmed: boolean) => {
    void setIsConfirmed(confirmed)
  }

  return (
    <Box
      data-test-id="details-step-content"
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        height: '100%',
        overflowY: 'scroll',
        scrollbarGutter: 'stable both-edges',
        pr: '8px',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          marginLeft: '4px'
        }
      }}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px', flex: 1}}>
        {regionFeatures.canConfirmOrder && (
          <OrderConfirmationSection
            onConfirmationChange={handleConfirmationChange}
            disabled={isConfirmationDisabled}
            disabledMessage={
              !canConfirmOrder && !confirmationPinRequired
                ? 'orderIntake.rmc.orderManagement.confirmation.contactDispatch'
                : undefined
            }
            isPinRequired={confirmationPinRequired}
          />
        )}
        {/* Material Testing Field - Only show if materialTesting feature is enabled */}
        {(regionFeatures.hasTesting || detailsFields.includes('testing')) && (
          <MaterialTestingField />
        )}

        {(regionFeatures.hasAdmixtures || detailsFields.includes('retarder')) && <RetarderField />}

        <PoNumberField />

        {(regionFeatures.hasDriverInstructions || detailsFields.includes('driverInstructions')) && (
          <DriverInstructionsField />
        )}

        {(regionFeatures.hasNotes || detailsFields.includes('additionalNotes')) && <NotesField />}

        <ContactSection userProfileCountry={userProfileCountry} />
      </Box>
    </Box>
  )
}
